import * as Headless from '@headlessui/react';
import {
  OtsComboboxOptions,
  isOtsComboboxOptionDisabled,
} from './OtsComboboxOptions';
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { useId, useMemo, useState } from 'react';
import {
  getOtsFormControlClassName,
  OtsFormField,
  OtsFormFieldSize,
} from './OtsFormField';

export type OtsComboboxOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type OtsComboboxProps = {
  id?: string;
  name?: string;
  label: string;
  description?: string;
  options: OtsComboboxOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  loading?: boolean;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  size?: OtsFormFieldSize;
  emptyMessage?: string;
  className?: string;
};

/** Searchable Catalyst-style combobox with OTS tokens and compact bridge support. */
export const OtsCombobox = ({
  id: idProp,
  name,
  label,
  description,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  loading = false,
  error,
  disabled = false,
  required,
  size = 'default',
  emptyMessage = 'No matching options.',
  className,
}: OtsComboboxProps) => {
  const generatedId = useId();
  const id = idProp ?? `ots-combobox-${generatedId}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ');
  const [query, setQuery] = useState('');
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value]
  );
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return normalized
      ? options.filter(
          (option) =>
            option.label.toLocaleLowerCase().includes(normalized) ||
            option.value.toLocaleLowerCase().includes(normalized) ||
            option.description?.toLocaleLowerCase().includes(normalized)
        )
      : options;
  }, [options, query]);
  const unavailable = disabled || loading;

  const renderOption = (option: OtsComboboxOption) => (
    <Headless.ComboboxOption
      key={option.value}
      value={option}
      disabled={option.disabled}
      className={[
        'group flex cursor-default items-center gap-2 rounded-md px-2.5 data-focus:bg-[var(--ots-primary-container)] data-focus:text-[var(--ots-on-primary-container)] data-disabled:opacity-50',
        size === 'compact' ? 'min-h-8 text-xs' : 'min-h-9 text-sm',
      ].join(' ')}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{option.label}</span>
        {option.description && (
          <span className="block truncate text-[11px] text-[var(--ots-text-muted)] group-data-focus:text-inherit">
            {option.description}
          </span>
        )}
      </span>
      <CheckIcon
        aria-hidden="true"
        className="invisible size-4 shrink-0 group-data-selected:visible"
      />
    </Headless.ComboboxOption>
  );

  return (
    <OtsFormField
      htmlFor={id}
      label={label}
      description={description}
      descriptionId={descriptionId}
      error={error}
      errorId={errorId}
      required={required}
      size={size}
      className={className}
    >
      {name && (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          disabled={unavailable}
        />
      )}
      <Headless.Combobox
        value={selected}
        onChange={(option) => onChange(option?.value)}
        disabled={unavailable}
        onClose={() => setQuery('')}
        virtual={{ options: filtered, disabled: isOtsComboboxOptionDisabled }}
      >
        <div className="relative min-w-0">
          <Headless.ComboboxInput
            id={id}
            aria-label={label}
            required={required}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : undefined}
            displayValue={(option: OtsComboboxOption | null) =>
              option?.label ?? ''
            }
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={loading ? 'Loading options…' : placeholder}
            className={getOtsFormControlClassName(
              size,
              selected && !required ? 'pr-16' : 'pr-9'
            )}
          />
          {selected && !required && !unavailable && (
            <button
              type="button"
              aria-label={`Clear ${label}`}
              className="absolute inset-y-0 right-8 flex items-center px-1 text-[var(--ots-text-muted)] hover:text-[var(--ots-text)]"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChange(undefined)}
            >
              <XMarkIcon aria-hidden="true" className="size-4" />
            </button>
          )}
          <Headless.ComboboxButton
            aria-label={`Open ${label} options`}
            className="absolute inset-y-0 right-0 flex items-center px-2 text-[var(--ots-text-muted)] hover:text-[var(--ots-text)] disabled:cursor-not-allowed"
          >
            <ChevronUpDownIcon aria-hidden="true" className="size-4" />
          </Headless.ComboboxButton>
          <OtsComboboxOptions
            options={filtered}
            renderOption={renderOption}
            emptyMessage={emptyMessage}
            className="absolute z-50 mt-1 max-h-60 w-full min-w-0 overflow-auto rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-1 text-[var(--ots-text)] shadow-xl focus:outline-none"
          />
        </div>
      </Headless.Combobox>
      {loading && (
        <p role="status" className="text-xs text-[var(--ots-text-muted)]">
          Loading options…
        </p>
      )}
    </OtsFormField>
  );
};
